import React, { useCallback, useMemo, useState } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { RJSFSchema, UiSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import debounce from 'lodash/debounce';
import { WithSnackbarProps, withSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import ConfirmButton from '~/components/Common/Elements/ConfirmButton';
import ErrorDisplay from '~/components/Common/ErrorDisplay';
import cleanProperty from '~/components/Vendors/Properties/helpers/cleanProperty';

import { PROPERTY_CHANNEL_MANAGERS } from '~/consts/reservation';

import BedbankService from '~/services/BedbankService';
import ReservationService from '~/services/ReservationService';
import { formatDateISO } from '~/services/TimeService';

import deleteNulls from '~/utils/deleteNulls';
import featureToggle from '~/utils/featureToggle';
import { reportError } from '~/utils/reportError';

import AsyncAutocomplete from '../Legacy/AsyncAutocomplete';
import PropertyMap from '../PropertyMap';

import ImageUploadField from './ImageUploadField';
import { buttonMessages, buttonStates } from './states/submitButton';

const uiSchema: UiSchema = {
  id_salesforce_external: { 'ui:widget': 'hidden' },
  logo_id_cloudinary_external: {
    'ui:title': 'Property logo',
    'ui:widget': (props) => (
      <ImageUploadField
        label="logo"
        field_key={props.id}
        value={props.value}
        onUpload={(field_key, newValue) => {
          props.onChange(newValue);
        }}
      />
    ),
  },
  'ui:order': ['*', 'parent_property_id', 'bedbank_property_id', 'logo_id_cloudinary_external', 'geo_data'],
  max_child_age: {
    'ui:help': 'The age up to which a child is classified as a child',
  },
  max_infant_age: {
    'ui:help': 'The age up to which a child is classified as an infant',
  },
  max_teenager_age: {
    'ui:widget': (props) => (
      <Box>
        <FormControlLabel
          label="Enable Teenager Age Limit"
          control={
            <Checkbox
              checked={props.value !== null && props.value !== '' && props.value !== undefined}
              onChange={(e) => {
                props.onChange(e.target.checked ? 17 : null);
              }}
            />
          }
        />
        {props.value !== null && props.value !== '' && props.value !== undefined && (
          <TextField
            label="Max age of Teenagers"
            type="number"
            value={props.value}
            onChange={(e) => props.onChange(e.target.value)}
            fullWidth
            margin="normal"
            helperText="The age up to which a child is classified as a teenager"
          />
        )}
      </Box>
    ),
  },
  geo_data: {
    'ui:classNames': 'geo-field',
  },
  regions_marketed_los: {
    'ui:options': {
      addable: false,
      orderable: false,
      removable: false,
    },
  },
  location_heading: {
    'ui:title': 'Location City',
  },
  location_subheading: {
    'ui:title': 'Location Country/State',
  },
};

const guidReg = new RegExp('\\b[0-9a-f]{8}\\b-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-\\b[0-9a-f]{12}\\b');

const propertyKeysToDeleteIfNull = ['hotel_meta_provider', 'pms', 'max_teenager_age'];

const contentEnabled = featureToggle.availableToShow('ADDITIONAL_PROPERTY_CONTENT_ENABLED');

function formExtraValidation(formData, errors) {
  if (formData.max_child_age <= formData.max_infant_age) {
    errors.max_child_age.addError('Max age of Children must be higher than Max age of Infants');
  }

  if (!Number.isInteger(formData.max_child_age)) {
    errors.max_child_age.addError('Max age of Children must be integer');
  }

  if (
    formData.max_teenager_age !== null &&
    formData.max_teenager_age !== undefined &&
    !Number.isInteger(formData.max_teenager_age)
  ) {
    errors.max_teenager_age.addError('Max age of Teenagers must be integer');
  }

  return errors;
}

type Props = WithSnackbarProps & {
  property: unknown;
  schema: RJSFSchema;
  vendorId: string;
};

function PropertyForm({ property: initialProperty, schema: initialSchema, vendorId, enqueueSnackbar }: Props) {
  const history = useHistory();

  const [property, setProperty] = useState(cleanProperty(deleteNulls(initialProperty, propertyKeysToDeleteIfNull)));
  const [saveState, setSaveState] = useState(buttonStates.default);
  const [errorMsg, setErrorMsg] = useState('');
  const [bedbankPropertyInvalid, setBedbankPropertyInvalid] = useState(false);

  // Create a copy of the schema to modify
  const schema = useMemo(() => {
    const schemaCopy = { ...initialSchema };
    delete schemaCopy.properties.taxes_and_fees_content;
    delete schemaCopy.properties.commissionable_taxes_and_fees;
    delete schemaCopy.properties.taxes_and_fees;
    return schemaCopy;
  }, [initialSchema]);

  const debouncedSetProperty = useMemo(
    () =>
      debounce((newProperty) => {
        setProperty(newProperty);
      }, 150),
    [],
  );

  const onSubmit = (form: IChangeEvent) => {
    form.errors = [];
    form.errorSchema = {};

    setSaveState(buttonStates.saving);
    setProperty(form.formData);

    let submitType = 'createProperty';
    let id = null;
    if (property.id) {
      submitType = 'updateProperty';
      id = property.id;
    }

    if (form.formData.channel_manager !== PROPERTY_CHANNEL_MANAGERS.derbysoft) {
      delete form.formData.channel_ari_type;
      delete form.formData.channel_rate_type;
      delete form.formData.send_nightly_reservation_rates;
      delete form.formData.generate_infant_capacities_from_children;
    }

    if (form.formData.channel_manager === PROPERTY_CHANNEL_MANAGERS.selfManaged) {
      form.formData.regions_marketed_los = [];
    }

    // Don't send large room_types data as it is not on this form
    const formDataWithoutRoomTypes = { ...form.formData };
    if ('room_types' in formDataWithoutRoomTypes) {
      delete formDataWithoutRoomTypes['room_types'];
    }

    ReservationService[submitType](formDataWithoutRoomTypes, id)
      .then((response) => {
        history.push(`/vendors/${vendorId}/properties/${response.id}`);
      })
      .catch((e) => {
        if (e.name === 'ValidationError' && e.errors[0]?.message === 'bedbank_property_id must be unique') {
          setBedbankPropertyInvalid(true);
          setSaveState(buttonStates.failed);
        } else {
          setSaveState(buttonStates.failed);
          reportError(e);
        }
      });
  };

  const handleUiChanges = useCallback((newProperty, currentProperty) => {
    if (!newProperty.hotel_meta_provider && !!currentProperty.hotel_meta_code) {
      newProperty.hotel_meta_code = '';
    }
    if (
      (!newProperty.channel_manager || newProperty.channel_manager === PROPERTY_CHANNEL_MANAGERS.selfManaged) &&
      !!currentProperty.hotel_code
    ) {
      newProperty.hotel_code = '';
    }
    if (newProperty.channel_manager !== PROPERTY_CHANNEL_MANAGERS.derbysoft && !!currentProperty.channel_supplier_id) {
      newProperty.channel_supplier_id = '';
    }
    if (newProperty.channel_manager !== PROPERTY_CHANNEL_MANAGERS.travelclick) {
      delete newProperty.pms;
    }
    if (newProperty.channel_manager === PROPERTY_CHANNEL_MANAGERS.travelclick && !newProperty.pms) {
      delete newProperty.pms;
    }
  }, []);

  const onChange = useCallback(
    (edit: IChangeEvent) => {
      const newProperty = edit.formData;

      if (saveState !== buttonStates.default) {
        setSaveState(buttonStates.default);
      }

      const hasUiChanges =
        newProperty.channel_manager !== property.channel_manager ||
        newProperty.hotel_meta_provider !== property.hotel_meta_provider;

      if (hasUiChanges) {
        handleUiChanges(newProperty, property);
        setProperty(newProperty);
      } else {
        debouncedSetProperty(newProperty);
      }
    },
    [saveState, debouncedSetProperty, property, handleUiChanges],
  );

  const onDelete = () => {
    ReservationService.deleteProperty(property.id)
      .then(() => {
        history.push(`/vendors/${vendorId}`);
      })
      .catch((e) => {
        reportError(e);
      });
  };

  const onRestore = () => {
    ReservationService.restoreProperty(property.id)
      .then(() => {
        history.push(`/vendors/${vendorId}`);
      })
      .catch((e) => {
        reportError(e);
      });
  };

  const onMapClick = (latitude: string, longitude: string) => {
    // TODO: save the place ID (future enhancement)

    const edit = {
      formData: Object.assign({}, property, {
        latitude,
        longitude,
      }),
    };
    onChange(edit as IChangeEvent);
  };

  const onReverseGeocode = (result) => {
    const edit = {
      formData: Object.assign({}, property, {
        geo_data: result,
      }),
    };
    onChange(edit as IChangeEvent);
  };

  const addLogo = (fieldKey, imageId) => {
    const edit = {
      formData: Object.assign({}, property),
    };
    edit.formData[fieldKey] = imageId;
    onChange(edit as IChangeEvent);
  };

  const getParentPropertyPredictions = (input, callback) => {
    if (!input) {
      return callback([]);
    }
    if (guidReg.test(input)) {
      ReservationService.getProperty(input).then((prediction) => {
        if (!prediction || prediction.result.parent_property_id) {
          return callback([]);
        }
        return callback([prediction.result]);
      });
    } else {
      const limit = 100;
      const page = 1;
      const includeDeleted = false;
      ReservationService.getPropertiesByName(input, limit, page, includeDeleted).then((predictions) => {
        if (!predictions) {
          return callback([]);
        }
        const filtered = predictions.result.filter((property) => !property.parent_property_id);
        return callback(filtered);
      });
    }
  };

  const debouncedGetParentPredictions = debounce(getParentPropertyPredictions, 500);

  const onChangeWithParentProperty = (selected) => {
    if (selected) {
      setProperty({
        ...property,
        parent_property_id: selected.id,
        parentProperty: selected,
      });
    }
  };

  const clearParentProperty = () => {
    const newProperty = { ...property };
    delete newProperty.parent_property_id;
    delete newProperty.parentProperty;
    setProperty(newProperty);
  };

  const getBedbankPropertyPredictions = (input: string, callback) => {
    if (!input) {
      return callback([]);
    }

    const params = {
      query: input,
      type: BedbankService.isBedbankId(input) ? 'id' : 'name',
    };

    BedbankService.getProperties(1, 20, params)
      .then((predictions) => {
        if (!predictions) {
          return callback([]);
        }
        return callback(predictions.result);
      })
      .catch((error) => {
        enqueueSnackbar(error.message || 'Error! Unable to fetch properties.', {
          variant: 'error',
        });
      });
  };

  const debouncedGetBedbankPredictions = debounce(getBedbankPropertyPredictions, 500);

  const onChangeWithBedbankProperty = (selected) => {
    if (selected) {
      setBedbankPropertyInvalid(false);
      setProperty({
        ...property,
        bedbank_property_id: selected.id,
        bedbankProperty: selected,
      });
    }
  };

  const clearBedbankProperty = () => {
    const newProperty = { ...property };
    delete newProperty.bedbank_property_id;
    delete newProperty.bedbankProperty;
    setProperty(newProperty);
  };

  const noOptionsMessage = (i) => {
    return !i.inputValue ? 'Search for a property by name or hotel code' : 'Not Found';
  };

  const mapParentPropertyLabel = (property) => {
    if (!property) {
      return '';
    }

    return `
      ${property.name} | Vendor: ${property.id_salesforce_external}
      ${property.channel_manager ? ` | CM: ${property.channel_manager}` : ''}
      ${property.hotel_code ? ` | Hotel Code: ${property.hotel_code}` : ''}
      | Created: ${formatDateISO(property.created_at)}`;
  };

  let deleteButton = null;
  let restoreButton = null;

  if (property.id) {
    if (property.deleted_at) {
      restoreButton = (
        <ConfirmButton confirmTitle="Restore property" confirmAnswer="Yes, continue" onConfirm={onRestore}>
          Restore Property
        </ConfirmButton>
      );
    } else {
      deleteButton = (
        <ConfirmButton
          confirmTitle="Delete property"
          confirmQuestion="Are you sure you want to delete this property?"
          confirmAnswer="Yes, delete"
          onConfirm={onDelete}
        >
          Delete
        </ConfirmButton>
      );
    }
  }

  const formProperty = Object.assign({}, property);

  if (typeof formProperty.latitude !== 'number' && typeof formProperty.latitude !== 'string') {
    delete formProperty.latitude;
  }

  if (typeof formProperty.longitude !== 'number' && typeof formProperty.longitude !== 'string') {
    delete formProperty.longitude;
  }

  if (!formProperty.logo_id_cloudinary_external) {
    delete formProperty.logo_id_cloudinary_external;
  }

  const currentUiSchema = { ...uiSchema };

  currentUiSchema.parent_property_id = {
    'ui:title': 'Parent Property',
    'ui:widget': (props) => (
      <>
        {props.registry.formContext?.parentProperty && (
          <Stack mb={1} direction="row" spacing={2} alignItems="center">
            <Typography>{props.registry.formContext.parentProperty.name}</Typography>
            <IconButton aria-label="Remove" onClick={clearParentProperty}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        )}

        <AsyncAutocomplete
          loadOptions={debouncedGetParentPredictions}
          onChange={onChangeWithParentProperty}
          getOptionLabel={mapParentPropertyLabel}
          placeholder="Search for a property by name or hotel code"
          label="Parent property"
        />
      </>
    ),
  };

  currentUiSchema.bedbank_property_id = {
    'ui:title': 'Bedbank Property',
    'ui:widget': (props) => (
      <>
        {props.registry.formContext?.bedbankProperty && (
          <Stack mb={1} direction="row" spacing={2} alignItems="center">
            <Typography>{props.registry.formContext.bedbankProperty.name}</Typography>
            <IconButton aria-label="Remove" onClick={clearBedbankProperty}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        )}
        {bedbankPropertyInvalid && (
          <Alert severity="warning">This property is already linked to another property</Alert>
        )}

        <AsyncAutocomplete
          loadOptions={debouncedGetBedbankPredictions}
          onChange={onChangeWithBedbankProperty}
          getOptionLabel={(property: App.Property) => property.name}
          placeholder="Search for a property by name or hotel code"
          label="Bedbank property"
        />
      </>
    ),
  };

  currentUiSchema.hotel_meta_code = {
    'ui:widget': property.hotel_meta_provider ? 'text' : 'hidden',
  };

  currentUiSchema.hotel_code = {
    'ui:widget':
      property.channel_manager !== PROPERTY_CHANNEL_MANAGERS.selfManaged && !!property.channel_manager
        ? 'text'
        : 'hidden',
  };

  currentUiSchema.channel_supplier_id = {
    'ui:widget': property.channel_manager === PROPERTY_CHANNEL_MANAGERS.derbysoft ? 'text' : 'hidden',
  };

  if (property.channel_manager !== PROPERTY_CHANNEL_MANAGERS.derbysoft) {
    currentUiSchema.channel_ari_type = {
      'ui:widget': 'hidden',
    };
    currentUiSchema.channel_rate_type = {
      'ui:widget': 'hidden',
    };
    currentUiSchema.send_nightly_reservation_rates = {
      'ui:widget': 'hidden',
    };
    currentUiSchema.generate_infant_capacities_from_children = {
      'ui:widget': 'hidden',
    };
  } else {
    currentUiSchema.channel_ari_type = schema.properties.channel_ari_type;
    currentUiSchema.channel_rate_type = schema.properties.channel_rate_type;
    currentUiSchema.send_nightly_reservation_rates = schema.properties.send_nightly_reservation_rates;
    currentUiSchema.generate_infant_capacities_from_children =
      schema.properties.generate_infant_capacities_from_children;
  }

  if (property.channel_manager !== PROPERTY_CHANNEL_MANAGERS.travelclick) {
    currentUiSchema.pms = {
      'ui:widget': 'hidden',
    };
  } else {
    currentUiSchema.pms = schema.properties.pms;
  }

  if (!contentEnabled) {
    currentUiSchema.videos = {
      'ui:widget': 'hidden',
    };
  }

  return (
    <Form
      schema={schema}
      onChange={onChange}
      formData={formProperty}
      onSubmit={onSubmit}
      uiSchema={currentUiSchema}
      formContext={formProperty}
      customValidate={formExtraValidation}
      validator={validator}
    >
      <Box mt={2}>
        <label>Select location</label>
        <PropertyMap
          name={formProperty.name}
          address={formProperty.address}
          lat={formProperty.latitude}
          lng={formProperty.longitude}
          onMapClick={onMapClick}
          reverseGeocode
          onReverseGeocode={onReverseGeocode}
          setErrorMsg={setErrorMsg}
        />
      </Box>

      {errorMsg && (
        <Box mt={2}>
          <ErrorDisplay message={errorMsg} />
        </Box>
      )}

      <Box className="button-container">
        <Stack direction="row" spacing={1}>
          <Button type="submit" className={saveState} variant="contained">
            {buttonMessages[saveState]}
          </Button>
          {restoreButton ? restoreButton : deleteButton}
        </Stack>
      </Box>
    </Form>
  );
}

export default withSnackbar(PropertyForm);
